import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pizza-premium-2/artifacts/he2ik45u_logo1.png";

const Footer = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  const socialLinks = [
    { icon: Facebook, url: "https://facebook.com/pizzadonvito", label: "Facebook" },
    { icon: Instagram, url: "https://instagram.com/pizzadonvito", label: "Instagram" }
  ];

  const locations = [
    { name_bg: "Don Vito Красно село", name_en: "Don Vito Krasno Selo", phone: "+359 89 854 5464" },
    { name_bg: "Don Vito Красна поляна", name_en: "Don Vito Krasna Polyana", phone: "+359 89 862 2422" }
  ];

  return (
    <footer className={`border-t py-12 transition-colors duration-300 ${
      isDark 
        ? 'bg-[#0A0A0A] border-white/10' 
        : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Logo & Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <img 
              src={LOGO_URL} 
              alt="Don Vito" 
              className={`h-14 w-auto mb-4 ${!isDark && 'brightness-0'}`}
            />
            <p className={`font-outfit text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('footer_text')}
            </p>
          </motion.div>

          {/* Locations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className={`font-cormorant text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'bg' ? 'Локации' : 'Locations'}
            </h3>
            <div className="space-y-3">
              {locations.map((location, index) => (
                <div key={index} className="flex items-start gap-3">
                  <MapPin className="text-[#C8102E] w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={`font-outfit text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'bg' ? location.name_bg : location.name_en}
                    </p>
                    <a 
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className={`font-outfit text-sm hover:text-[#008000] transition-colors flex items-center gap-1 ${
                        isDark ? 'text-white/60' : 'text-gray-500'
                      }`}
                    >
                      <Phone size={12} />
                      {location.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className={`font-cormorant text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('follow_us')}
            </h3>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={index}
                    data-testid={`social-${social.label.toLowerCase()}`}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-[#008000] hover:text-white ${
                      isDark 
                        ? 'bg-white/10 text-white/80' 
                        : 'bg-gray-200 text-gray-600'
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    <IconComponent size={20} />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-10 pt-6 border-t text-center ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          <p className={`font-outfit text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} Pizza Don Vito. {language === 'bg' ? 'Всички права запазени.' : 'All rights reserved.'}
          </p>
          <p className={`font-outfit text-base mt-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            Designed by{' '}
            <a 
              href="https://www.instagram.com/digitalei8ht/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold hover:opacity-80 transition-opacity"
            >
              <span className={isDark ? 'text-white' : 'text-gray-900'}>Digital</span>
              <span style={{ color: '#9b6bcf' }}>Eight</span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

